import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Tooltip,
    Typography,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Download, ExpandMore, ExpandLess, CheckCircle, Error, Person, CalendarToday, Info } from '@mui/icons-material';
import { format } from 'date-fns';
import { gql, useQuery } from '@apollo/client';

const GET_LEADGEN_IMPORT_HISTORY = gql`
    query GetLeadGenImportHistory($eventId: String!) {
        leadGenImportHistory(eventId: $eventId)
    }
`;

export default function LeadGenImportHistory({ eventId }) {
    const [expandedItems, setExpandedItems] = useState({});
    const { loading, error, data } = useQuery(GET_LEADGEN_IMPORT_HISTORY, {
        variables: { eventId },
    });

    let history = [];
    try {
        if (data?.leadGenImportHistory) {
            const parsed =
                typeof data.leadGenImportHistory === 'string'
                    ? JSON.parse(data.leadGenImportHistory)
                    : data.leadGenImportHistory;
            history = Array.isArray(parsed) ? parsed : [];
        }
    } catch (e) {
        console.error('Error parsing import history:', e);
        history = [];
    }

    const toggleExpand = id => {
        setExpandedItems(prev => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const getUserFriendlyError = reason => {
        const errorMap = {
            missing_id: 'ID is required for each stand',
            missing_company: 'Company name is required',
            missing_email: 'Email address is required',
            missing_password: 'Password is required',
            missing_allowed_no_of_staff: 'Allowed number of staff is required',
            invalid_email: 'Please provide a valid email address',
            duplicate_id: 'This ID already exists. Please use a unique ID',
            duplicate_email: 'This email already exists. Please use a unique email',
            invalid_staff_count: 'Allowed number of staff must be a valid number',
            bad_characters: 'Invalid characters found in data. Please check and remove special characters',
        };
        return errorMap[reason] || reason || 'An unexpected error occurred';
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant='body2' sx={{ mt: 2 }}>
                    Loading import history...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity='error' sx={{ m: 2 }}>
                Error loading import history: {error.message}
            </Alert>
        );
    }

    if (history.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Info color='disabled' sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant='h6' color='text.secondary'>
                    No import history found
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                    Import history will appear here after you upload stands data.
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {history.map((item, index) => {
                const isExpanded = expandedItems[item.id || index];
                let failedRows = [];
                let successfulRows = [];

                try {
                    if (item.failed_data) {
                        const parsed =
                            typeof item.failed_data === 'string' ? JSON.parse(item.failed_data) : item.failed_data;
                        failedRows = Array.isArray(parsed) ? parsed : [];
                    }
                    if (item.imported_data) {
                        const parsed =
                            typeof item.imported_data === 'string'
                                ? JSON.parse(item.imported_data)
                                : item.imported_data;
                        successfulRows = Array.isArray(parsed) ? parsed : [];
                    }
                } catch (e) {
                    console.error('Error parsing history item data:', e);
                }

                const successRate =
                    item.total_rows > 0 ? ((item.successful_rows / item.total_rows) * 100).toFixed(1) : 0;

                return (
                    <Card key={item.id || index} elevation={2}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                                    <Box>
                                        <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 1 }}>
                                            <Typography variant='h6' fontWeight={600}>
                                                Import #{history.length - index}
                                            </Typography>
                                            <Chip
                                                label={item.successful_rows > 0 ? 'Success' : 'Failed'}
                                                color={item.successful_rows > 0 ? 'success' : 'error'}
                                                size='small'
                                            />
                                        </Stack>
                                        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
                                            <Stack direction='row' spacing={1} alignItems='center'>
                                                <Person fontSize='small' color='action' />
                                                <Typography variant='body2' color='text.secondary'>
                                                    {item.uploaded_by || 'Unknown'}
                                                </Typography>
                                            </Stack>
                                            <Stack direction='row' spacing={1} alignItems='center'>
                                                <CalendarToday fontSize='small' color='action' />
                                                <Typography variant='body2' color='text.secondary'>
                                                    {item.created
                                                        ? format(new Date(item.created), 'PPp')
                                                        : 'Unknown date'}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                    <IconButton onClick={() => toggleExpand(item.id || index)} size='small'>
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                </Stack>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Paper sx={{ p: 1.5, bgcolor: 'info.light', textAlign: 'center' }}>
                                            <Typography variant='h5' color='white'>
                                                {item.total_rows || 0}
                                            </Typography>
                                            <Typography variant='caption' color='white'>
                                                Total Rows
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper sx={{ p: 1.5, bgcolor: 'success.light', textAlign: 'center' }}>
                                            <Typography variant='h5' color='white'>
                                                {item.successful_rows || 0}
                                            </Typography>
                                            <Typography variant='caption' color='white'>
                                                Successful ({successRate}%)
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper sx={{ p: 1.5, bgcolor: 'error.light', textAlign: 'center' }}>
                                            <Typography variant='h5' color='white'>
                                                {item.failed_rows || 0}
                                            </Typography>
                                            <Typography variant='caption' color='white'>
                                                Failed
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Collapse in={isExpanded}>
                                    <Divider sx={{ my: 2 }} />
                                    {failedRows.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant='subtitle1' gutterBottom fontWeight={600}>
                                                Failed Rows ({failedRows.length})
                                            </Typography>
                                            <TableContainer
                                                component={Paper}
                                                variant='outlined'
                                                sx={{ maxHeight: 400 }}
                                            >
                                                <Table size='small' stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Row #</TableCell>
                                                            <TableCell>ID</TableCell>
                                                            <TableCell>Company</TableCell>
                                                            <TableCell>Email</TableCell>
                                                            <TableCell>Error</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {failedRows.slice(0, 20).map((row, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{row.rowNumber || idx + 1}</TableCell>
                                                                <TableCell>{row.id || '-'}</TableCell>
                                                                <TableCell>{row.company || '-'}</TableCell>
                                                                <TableCell>{row.email || '-'}</TableCell>
                                                                <TableCell>
                                                                    <Typography variant='caption' color='error'>
                                                                        {getUserFriendlyError(row.reason || row.error)}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                                {failedRows.length > 20 && (
                                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                                        <Typography variant='caption' color='text.secondary'>
                                                            ... and {failedRows.length - 20} more failed rows
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </TableContainer>
                                        </Box>
                                    )}
                                    {successfulRows.length > 0 && (
                                        <Box>
                                            <Typography variant='subtitle1' gutterBottom fontWeight={600}>
                                                Successful Rows ({successfulRows.length})
                                            </Typography>
                                            <TableContainer
                                                component={Paper}
                                                variant='outlined'
                                                sx={{ maxHeight: 400 }}
                                            >
                                                <Table size='small' stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Row #</TableCell>
                                                            <TableCell>ID</TableCell>
                                                            <TableCell>Company</TableCell>
                                                            <TableCell>Email</TableCell>
                                                            <TableCell>Status</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {successfulRows.slice(0, 20).map((row, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{row.rowNumber || idx + 1}</TableCell>
                                                                <TableCell>{row.id || '-'}</TableCell>
                                                                <TableCell>{row.company || '-'}</TableCell>
                                                                <TableCell>{row.email || '-'}</TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={row.status || 'Success'}
                                                                        color='success'
                                                                        size='small'
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                                {successfulRows.length > 20 && (
                                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                                        <Typography variant='caption' color='text.secondary'>
                                                            ... and {successfulRows.length - 20} more successful rows
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </TableContainer>
                                        </Box>
                                    )}
                                </Collapse>
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
}
